import { noOp } from 'common/Constants';
import React, { useCallback } from 'react';
import { Checkmark } from 'src/shared/design-system/Icon';
import { ifProp } from 'src/shared/styled-utils/ifProp';
import styled, { css } from 'styled-components';
import { match } from 'ts-pattern';
import { Border, Colors, Font, Spacing, Transition } from 'web-common/src/shared/styles';

export interface ICheckboxProps {
  /** If the checkbox is checked. */
  checked?: boolean;
  /** Additional style class. */
  className?: string;
  /** Add data-cy tag for testing. */
  dataCy?: string;
  /** Add data-cy tag for the checkbox for testing */
  checkboxDataCy?: string;
  /** If the disabled is disabled. */
  disabled?: boolean;
  /** If the checkbox has an error. */
  error?: boolean;
  /** Color of the checkmark or circle filling a checked box */
  fillColor?: Colors;
  /** Id of the input. */
  id?: string;
  /** Use light text for default label color. */
  isLight?: boolean;
  /** If behaves as a radio button. */
  isRadio?: boolean;
  /** Name of the input. */
  name?: string;
  /** Callback when checkbox `checked` value changes. 1st arg is the change event & 2nd arg passed in the new checked value. */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  /** Label to display next to checkbox. */
  label?: React.ReactNode | string | number;
  /** Place label to the left of the checkbox. */
  labelOnLeft?: boolean;
  /** If checkbox is read only. */
  readOnly?: boolean;
}

type InputProps = {
  isRadio?: boolean;
};

const Container = styled.label`
  display: block;
`;

export const Wrapper = styled.div<{ disabled?: boolean }>`
  align-items: center;
  cursor: ${ifProp('disabled', 'default', 'pointer')};
  display: inline-flex;
  gap: ${Spacing.THREE_QUARTERS};
  position: relative;
`;

const Label = styled.div<{ isLight: boolean }>`
  color: ${ifProp('isLight', Colors.WHITE, Colors.GRAY_DARK_30)};
  font-size: ${Font.Size.S};
  transform: translateY(0.075em);
`;

const Input = styled.input.attrs(({ isRadio }: InputProps) => ({
  type: isRadio ? 'radio' : 'checkbox',
}))<InputProps>`
  position: absolute;
  opacity: 0 !important;
  height: 0 !important;
  width: 0 !important;
`;

interface IRadioCheckProps {
  fillColor?: Colors;
}

const RadioCheck = styled.div<IRadioCheckProps>`
  border-radius: 100%;
  height: ${Spacing.THREE_QUARTERS};
  width: ${Spacing.THREE_QUARTERS};

  ${({ fillColor }) =>
    css`
      background: ${fillColor || Colors.GRAY_DARK_30};
    `}
`;

const CheckmarkContainer = styled.div<ICheckboxProps>`
  align-items: center;
  background: ${Colors.WHITE};
  box-sizing: border-box;
  cursor: ${ifProp('disabled', 'not-allowed', 'inherit')};
  display: flex;
  justify-content: center;
  padding: calc(${Spacing.EIGHTH} + 1px);
  transition: border ${Transition.Time.BASE};

  ${ifProp(
    'isRadio',
    css`
      height: ${Spacing.SINGLE_HALF};
      min-height: ${Spacing.SINGLE_HALF};
      max-height: ${Spacing.SINGLE_HALF};
      width: ${Spacing.SINGLE_HALF};
      min-width: ${Spacing.SINGLE_HALF};
      max-width: ${Spacing.SINGLE_HALF};
    `,
    css`
      height: ${Spacing.SINGLE_QUARTER};
      min-height: ${Spacing.SINGLE_QUARTER};
      max-height: ${Spacing.SINGLE_QUARTER};
      width: ${Spacing.SINGLE_QUARTER};
      min-width: ${Spacing.SINGLE_QUARTER};
      max-width: ${Spacing.SINGLE_QUARTER};
    `
  )};

  ${({ checked, disabled, error, fillColor, isRadio }) =>
    match({ checked, disabled, error })
      .with(
        { error: true },
        () => css`
          border: ${Border.Base} ${Colors.RADISH};
        `
      )
      .with(
        { disabled: true },
        () => css`
          border: ${Border.Base} ${Colors.GRAY};
          color: ${Colors.GRAY};

          ${Container}:hover &, &:hover {
            border: ${Border.Base} ${Colors.GRAY};
          }
          & + ${Label} {
            color: ${Colors.GRAY};
          }
          ${isRadio &&
          `
            & > ${RadioCheck} {
              background: ${Colors.GRAY};
            }
          `}
        `
      )
      .with(
        { checked: true, disabled: false, error: false },
        () => css`
          border: ${Border.Base} ${Colors.BLACK};
          color: ${fillColor || Colors.RADISH};
        `
      )
      .otherwise(
        () => css`
          border: ${Border.Base} ${Colors.GRAY_DARK_10};
          ${Container}:hover & {
            border: ${Border.Base} ${Colors.BLACK};
          }
        `
      )}

  ${({ checked, isRadio }) =>
    match({ isRadio })
      .with(
        { isRadio: true },
        () => css`
          border-radius: ${Spacing.SINGLE_QUARTER};
          & + ${Label} {
            font-weight: 400;
          }
          & > ${RadioCheck} {
            opacity: ${checked ? 1 : 0};
            transform: scale(${checked ? 1 : 0.5});
            transition: opacity ${Transition.Time.BASE}, transform ${Transition.Time.HALF};
          }
        `
      )
      .otherwise(
        () => css`
          & + ${Label} {
            font-weight: 500;
          }
          & > svg {
            opacity: ${checked ? 1 : 0};
            transform: scale(${checked ? 1.2 : 0.5});
            transition: opacity ${Transition.Time.BASE}, transform ${Transition.Time.HALF};
          }
        `
      )}
`;

export const Checkbox: React.FC<ICheckboxProps> = (props) => {
  const {
    checked,
    className,
    dataCy,
    checkboxDataCy,
    disabled = false,
    error = false,
    id,
    isLight = false,
    isRadio = false,
    label,
    labelOnLeft,
    name,
    onChange = noOp,
    readOnly,
    fillColor,
  } = props;
  const handleChange = useCallback(
    (e) => {
      if (!disabled) {
        onChange(e, e.target.checked);
      }
    },
    [disabled, onChange]
  );
  const labelContents = label ? (
    <Label isLight={isLight} data-checked={checked}>
      {label}
    </Label>
  ) : null;

  return (
    <Container className={className} data-cy={dataCy}>
      <Wrapper disabled={disabled}>
        {labelOnLeft && labelContents}
        <Input
          checked={checked}
          disabled={disabled}
          id={id}
          isRadio={isRadio}
          name={name}
          onChange={handleChange}
          readOnly={readOnly}
        />
        <CheckmarkContainer
          checked={checked}
          disabled={disabled}
          error={error}
          isRadio={isRadio}
          fillColor={fillColor}
          data-cy={checkboxDataCy}>
          {isRadio ? <RadioCheck fillColor={fillColor} /> : <Checkmark />}
        </CheckmarkContainer>
        {!labelOnLeft && labelContents}
      </Wrapper>
    </Container>
  );
};
